import { reportMetrics } from './reportMetrics';
import { flowDuration } from './metrics/creates';
import { getRoute } from './route';

const IGNORED_URL_PATTERNS = [
  '/api/v3/telemetry',
];

function shouldIgnore(url) {
  return IGNORED_URL_PATTERNS.some(pattern => url.includes(pattern));
}

// Returns a stable filename for Prometheus label use.
// Strips content-hash suffixes so each deploy doesn't mint new label values:
//   main.a1b2c3d4.chunk.js  →  main.chunk.js
//   HomePageBanner.84bbb368.jpg  →  HomePageBanner.jpg
// data: URIs (inline base64) are collapsed to "(inline)".
function getResourceName(url) {
  if (!url || url.startsWith('data:')) return '(inline)';
  const filename = url.split('?')[0].split('/').pop() || '(root)';
  return filename.replace(/\.[a-f0-9]{7,}(\.chunk)?(\.[a-z0-9]+)$/i, '$1$2') || filename;
}

function getResourceType(entry) {
  const ext =
    entry.name
      .split('?')[0]
      .split('.')
      .pop()
      ?.toLowerCase() || '';
  if (['js', 'mjs'].includes(ext) || entry.initiatorType === 'script')
    return 'script';
  if (
    ['css'].includes(ext) ||
    entry.initiatorType === 'css' ||
    entry.initiatorType === 'link'
  )
    return 'stylesheet';
  if (
    ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'ico', 'avif'].includes(ext) ||
    entry.initiatorType === 'img'
  )
    return 'image';
  if (['woff', 'woff2', 'ttf', 'otf', 'eot'].includes(ext)) return 'font';
  return 'other';
}

// Buffer resource entries and flush periodically. Uses a debounced timer so
// that an idle page doesn't keep flushing tiny batches, while a busy page
// (e.g. SPA route change loading lots of new chunks) flushes regularly.
let pendingMetrics = [];
let flushTimer = null;
const FLUSH_DEBOUNCE_MS = 3000;

function flushMetrics() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (pendingMetrics.length === 0) return;
  const batch = pendingMetrics;
  pendingMetrics = [];
  reportMetrics(batch);
}

function queueResourceMetric(metric) {
  pendingMetrics.push(metric);
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(flushMetrics, FLUSH_DEBOUNCE_MS);
}

function collectResourceEntry(entry) {
  if (shouldIgnore(entry.name)) return;

  const resourceType = getResourceType(entry);
  const resourceName = getResourceName(entry.name);
  const duration = entry.responseEnd - entry.startTime;
  const transferSize = entry.transferSize || 0;
  const route = getRoute();

  if (duration > 0) {
    queueResourceMetric(
      flowDuration(duration, {
        flow: 'resource_load',
        resource_type: resourceType,
        ext1: resourceName,
        route,
      }),
    );
  }

  if (transferSize > 0) {
    queueResourceMetric(
      flowDuration(transferSize, {
        flow: 'resource_size',
        resource_type: resourceType,
        ext1: resourceName,
        route,
      }),
    );
  }
}

export function initResourceMetrics() {
  if (typeof window === 'undefined' || !window.PerformanceObserver) return;

  try {
    const buffered = performance.getEntriesByType('resource');
    buffered.forEach(collectResourceEntry);

    const observer = new PerformanceObserver(list => {
      for (const entry of list.getEntries()) {
        collectResourceEntry(entry);
      }
    });

    observer.observe({ type: 'resource', buffered: false });

    // Force-flush remaining queued metrics before the page is suspended/closed.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        flushMetrics();
      }
    });
  } catch (e) {
    console.warn('[@shopeepay/reporter] Resource metrics not supported:', e);
  }
}
